import React, { Suspense, useCallback, useEffect, useMemo } from "react";

import { faArrowToBottom } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { graphql } from "relay-runtime";

import type { ListeningPorts } from "~/lib/desktopApi/open-in-desktop-app-ports";
import { useArchitecture } from "~/lib/hooks";
import { hasOwnProperty } from "~/lib/js";
import { getDesktopURL } from "~/lib/open-in-desktop-app";
import { commitMutationPromise, useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import {
	desktopDownloadLink,
	inVideocallMode,
	isMacos,
	isMobile,
	isSafari,
	isWeb,
	macOsArmDesktopDownloadUri,
	macOsX64DesktopDownloadUri,
} from "~/lib/utils";
import { environment } from "~/models";
import { Button } from "~/ui";
import { ButtonLink, Link } from "~/ui/legacyFellowIndex";

import { ReactComponent as ArrowToDownloads } from "./images/arrow-to-downloads.svg";
import { ReactComponent as FellowDesktopIcon } from "./images/fellow-desktop-icon.svg";
import { ReactComponent as MacAppFolder } from "./images/mac-applications-folder.svg";
import { ReactComponent as Vector } from "./images/vector.svg";
import { JourneyContext } from "./JourneyContext";

import type { DesktopLaunchPageAutomatedLoginTicketMutation } from "~/__generated__/DesktopLaunchPageAutomatedLoginTicketMutation.graphql";
import type { DesktopLaunchPageLoginTicketMutation } from "~/__generated__/DesktopLaunchPageLoginTicketMutation.graphql";
import type { DesktopLaunchPageQuery as QueryType } from "~/__generated__/DesktopLaunchPageQuery.graphql";

import styles from "./css/DesktopLaunchPage.module.css";

const DesktopLaunchPageQuery = graphql`
	query DesktopLaunchPageQuery($platform: String!) {
		latestVersion(platform: $platform) {
			downloadFile
		}
	}
`;

export const DESKTOP_LAUNCH_PATH = "/o/desktop-launch/";

const LISTENING_PORTS: ListeningPorts = [65040, 52254, 60962];

export async function getOpenDesktopPort(): Promise<number | null | false> {
	if (isSafari) return null;
	for (const port of LISTENING_PORTS) {
		const url = getDesktopURL(port, "can-navigate", { url: `${window.location.origin}/auth/login/ticket/` });
		let response: Response;
		try {
			const rawResponse = await fetch(url.toString(), {
				mode: "cors",
				method: "GET",
			});

			if (!rawResponse.ok) continue;
			response = await rawResponse.json();
		} catch (e) {
			continue;
		}
		if (hasOwnProperty(response, "canNavigate") && typeof response.canNavigate === "boolean") {
			return response.canNavigate ? port : false;
		}
	}
	return null;
}

function DesktopAppVersion({ architecture }: { architecture: string }) {
	const queryVars = React.useMemo(() => {
		if (!isMacos) return { platform: "win32" };
		if (architecture == "arm") return { platform: "darwin_arm64" };
		if (architecture == "x64") return { platform: "darwin_x64" };
		return { platform: "darwin" };
	}, [architecture]);

	const response = useLazyLoadQuery<QueryType>(DesktopLaunchPageQuery, queryVars, {});
	return <>{response.latestVersion?.downloadFile.split("/").pop()}</>;
}

export default function DesktopLaunchPage() {
	const { journey, nextStep, goToFinalStep } = React.useContext(JourneyContext);
	const [desktopLoginSuccessful, setDesktopLoginSuccessful] = React.useState(false);
	const [showContinueOnWeb, setShowContinueOnWeb] = React.useState(false);

	const architecture = useArchitecture();

	const finalStepQueryParam = "goToFinalStep";
	let nextPath = useMemo(() => {
		let selfIndex = journey.indexOf(DESKTOP_LAUNCH_PATH);

		// if this is the last onboarding step, redirect to this page with a special query param that triggers
		// the goToFinalStep() function to be called
		if (selfIndex !== journey.length - 1) {
			return journey[selfIndex + 1];
		} else {
			return `${DESKTOP_LAUNCH_PATH}?${finalStepQueryParam}=1`;
		}
	}, [journey]);

	const inApplicableEnvironment = isWeb && !inVideocallMode && !isMobile && window.parent === window;

	useEffect(() => {
		async function inner() {
			const isInBrowserWithoutDetection = isSafari;
			const port = await getOpenDesktopPort();

			// -1 implies the app is running but doesn't support navigating to this endpoint, i.e for older versoions of the desktop app
			if (!inApplicableEnvironment || port === false) {
				clearInterval(pollDesktopPort);
				return;
			}
			if (port === null && !isInBrowserWithoutDetection) return;

			if (port) {
				clearInterval(pollDesktopPort);
				const result = await commitMutationPromise<DesktopLaunchPageAutomatedLoginTicketMutation>(environment, {
					mutation: graphql`
						mutation DesktopLaunchPageAutomatedLoginTicketMutation($path: String!) {
							generateLoginTicket(path: $path) {
								ticket
							}
						}
					`,
					variables: {
						path: nextPath,
					},
				});

				if (result.generateLoginTicket?.ticket) {
					// This will trigger the Fellow desktop app to open and auto-log in using the ticket
					const url = getDesktopURL(port, "open", { url: result.generateLoginTicket.ticket });
					await fetch(url.toString(), {
						method: "POST",
					});
					setDesktopLoginSuccessful(true);
				}
			}
		}
		const pollDesktopPort = setInterval(() => {
			inner();
		}, 500);

		return () => clearInterval(pollDesktopPort);
	}, [nextPath, inApplicableEnvironment]);

	const onContinueToApp = React.useCallback(async () => {
		setTimeout(() => {
			setShowContinueOnWeb(true);
		}, 10000);

		dispatchAnalyticsTrack("Launch Desktop App Clicked", { feature: "Onboarding" });

		// Generate a login ticket for the desktop app (expires in 30 seconds)
		const result = await commitMutationPromise<DesktopLaunchPageLoginTicketMutation>(environment, {
			mutation: graphql`
				mutation DesktopLaunchPageLoginTicketMutation($path: String!) {
					generateLoginTicket(path: $path) {
						ticket
					}
				}
			`,
			variables: {
				path: nextPath,
			},
		});

		if (result.generateLoginTicket?.ticket) {
			// This will trigger the Fellow desktop app to open and auto-log in using the ticket
			window.location.href = `fellow:${result.generateLoginTicket.ticket}`;
		}
	}, [nextPath]);

	useEffect(() => {
		const searchParams = new URLSearchParams(window.location.search);
		const shouldGoToFinalStep = searchParams.get(finalStepQueryParam) === "1";

		if (shouldGoToFinalStep) {
			goToFinalStep();
		}
	}, [goToFinalStep]);

	const downloadUrl = useMemo(() => {
		if (!isMacos) return desktopDownloadLink;
		if (architecture == "arm") return `${window.location.origin}${macOsArmDesktopDownloadUri}`;
		if (architecture == "x64") return `${window.location.origin}${macOsX64DesktopDownloadUri}`;
		return desktopDownloadLink;
	}, [architecture]);

	const onNextStep = useCallback(() => {
		nextStep();
	}, [nextStep]);

	return (
		<>
			{desktopLoginSuccessful ? (
				<div className={styles.installationCompleteContainer}>
					<div className={styles.installationCompleteHeader}>🎉 You&apos;re done!</div>
					<div className={styles.installationCompelteTitle}>You can now start using Fellow!</div>
					<div className={styles.installationCompelteText}>Feel free to close this window.</div>
				</div>
			) : (
				<>
					<div className={styles.headerContainer}>
						<div className={styles.title}>Done downloading? Next…</div>
					</div>
					<div className={styles.postDownloadStepsContainer}>
						<div className={styles.postDownloadStepCard}>
							<div className={styles.postDownloadStepCardNumber}>1</div>
							<div className={styles.postDownloadStepCardTitle}>Find the Fellow installer</div>
							<div className={styles.postDownloadStepCardText}>
								Click on the Downloads icon in your browser to find it.
							</div>
							<div className={cx(styles.backgroundShade, styles.flexColumn)}>
								<div className={styles.fileDownloadVersionContainer}>
									{architecture !== null && (
										<Suspense fallback={`Fellow-1.2.3.${isMacos ? "dmg" : "exe"}`}>
											<DesktopAppVersion architecture={architecture} />
										</Suspense>
									)}
									<FontAwesomeIcon icon={faArrowToBottom} className={styles.fileDownloadIcon} />
								</div>
								<div className={styles.retryDownload}>
									Can&apos;t find it?&nbsp;
									<Link href={downloadUrl} download>
										Retry download.
									</Link>
								</div>
							</div>
						</div>
						<div className={styles.postDownloadStepCard}>
							<div className={styles.postDownloadStepCardNumber}>2</div>
							<div className={styles.postDownloadStepCardTitle}>Open the Fellow installer</div>
							<div className={styles.postDownloadStepCardText}>
								Double-click to open and follow the steps to install Fellow.
							</div>
							<div className={styles.backgroundShade}>
								<div className={styles.appIconContainer}>
									<FellowDesktopIcon />
									<div className={styles.fellowDesktopIconText}>Fellow</div>
								</div>
								{isMacos && (
									<>
										<Vector className={styles.arrow} />
										<div className={styles.appIconContainer}>
											<MacAppFolder />
											<div className={styles.fellowDesktopIconText}>Applications</div>
										</div>
									</>
								)}
							</div>
						</div>
						<div className={styles.postDownloadStepCard}>
							<div className={cx(styles.postDownloadStepCardNumber, styles.hidden)}>3</div>
							<div className={styles.postDownloadStepCardTitle}>After installation…</div>
							<div className={styles.postDownloadStepCardText}>
								Launch the Fellow Desktop app by clicking the button below.
							</div>
							<Button variant="primary" className={styles.connectButton} onClick={onContinueToApp}>
								Open Fellow
							</Button>
						</div>
					</div>
					<div className={styles.footer}>
						{showContinueOnWeb && (
							<div className={styles.infoBox}>
								<span className={styles.infoBoxTitle}>Fellow app not launching?</span>
								<span className={styles.infoBoxCopy}>
									&nbsp;Make sure you follow the above steps to install Fellow,{" "}
									<ButtonLink onClick={onNextStep}>or continue on web.</ButtonLink>
								</span>
							</div>
						)}
					</div>
					<div className={styles.arrowToDownloads}>
						<ArrowToDownloads />
					</div>
				</>
			)}
		</>
	);
}
